import React, { useState, useEffect } from "react";
import { Box, IconButton, makeStyles } from "@material-ui/core";
import ChevronLeftRoundedIcon from "@material-ui/icons/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@material-ui/icons/ChevronRightRounded";
import colors from "../../../assets/v3/base/colors";

const useStyles = makeStyles((theme) => ({
  carouselContainer: {
    position: "relative",
    width: "100%",
    maxWidth: "1800px",
    margin: "0 auto",
    overflow: "visible",
    borderRadius: theme.spacing(2),
    [theme.breakpoints.down("xl")]: {
      maxWidth: "1200px",
    },
    [theme.breakpoints.down("lg")]: {
      maxWidth: "900px",
    },
    [theme.breakpoints.down("md")]: {
      maxWidth: "800px",
    },
  },
  carouselWrapper: {
    position: "relative",
    width: "100%",
    height: "100%",
    minHeight: "500px",
    maxHeight: "550px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
    [theme.breakpoints.down("lg")]: {
      minHeight: "450px",
      maxHeight: "500px",
    },
    [theme.breakpoints.down("md")]: {
      minHeight: "350px",
      maxHeight: "400px",
    },
  },
  slideContainer: {
    position: "absolute",
    width: "100%",
    height: "100%",
    opacity: 0,
    transition: "opacity 0.6s ease-in-out, transform 0.6s ease-in-out",
    transform: "scale(0.95)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
    padding: theme.spacing(2),
    "&.active": {
      opacity: 1,
      transform: "scale(1)",
      zIndex: 1,
    },
    "&.prev": {
      transform: "translateX(-100%) scale(0.9)",
      opacity: 0,
    },
    "&.next": {
      transform: "translateX(100%) scale(0.9)",
      opacity: 0,
    },
  },
  image: {
    maxWidth: "100%",
    maxHeight: "100%",
    width: "auto",
    height: "auto",
    objectFit: "contain",
    borderRadius: theme.spacing(1),
    display: "block",
    margin: "0 auto",
  },
  navigationButton: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    color: colors.gradients.tirumala.main,
    boxShadow: theme.shadows[4],
    "&:hover": {
      backgroundColor: colors.white.main,
      boxShadow: theme.shadows[8],
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  prevButton: {
    left: theme.spacing(2),
  },
  nextButton: {
    right: theme.spacing(2),
  },
  dotsContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
    padding: theme.spacing(1),
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: colors.grey[300],
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: colors.grey[400],
      transform: "scale(1.2)",
    },
  },
  activeDot: {
    backgroundColor: colors.gradients.tirumala.main,
    width: 12,
    height: 12,
    boxShadow: `0 0 8px ${colors.gradients.tirumala.main}`,
  },
}));

const HeroCarousel = ({ images = [], interval = 4000, autoPlay = true }) => {
  const classes = useStyles();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!autoPlay || images.length <= 1 || isPaused) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, interval);

    return () => clearInterval(timer);
  }, [images.length, interval, autoPlay, isPaused]);

  if (!images || images.length === 0) return null;

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const getSlideClass = (index) => {
    if (index === currentIndex) return "active";
    const prevIndex = (currentIndex - 1 + images.length) % images.length;
    const nextIndex = (currentIndex + 1) % images.length;
    if (index === prevIndex) return "prev";
    if (index === nextIndex) return "next";
    return "";
  };

  return (
    <Box
      className={classes.carouselContainer}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <Box className={classes.carouselWrapper}>
        {images.map((image, index) => (
          <Box
            key={index}
            className={`${classes.slideContainer} ${getSlideClass(index)}`}
          >
            <img
              src={image}
              alt={`Slide ${index + 1}`}
              className={classes.image}
            />
          </Box>
        ))}

        {images.length > 1 && (
          <>
            <IconButton
              className={`${classes.navigationButton} ${classes.prevButton}`}
              onClick={goToPrevious}
              aria-label="Previous slide"
            >
              <ChevronLeftRoundedIcon />
            </IconButton>
            <IconButton
              className={`${classes.navigationButton} ${classes.nextButton}`}
              onClick={goToNext}
              aria-label="Next slide"
            >
              <ChevronRightRoundedIcon />
            </IconButton>
          </>
        )}
      </Box>

      {images.length > 1 && (
        <Box className={classes.dotsContainer}>
          {images.map((_, index) => (
            <Box
              key={index}
              className={`${classes.dot} ${
                index === currentIndex ? classes.activeDot : ""
              }`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default HeroCarousel;

